// blueprintAssetTransfer — export ONE blueprint card to a file, and import a
// file back as a NEW card. The I/O shell; the rules live in
// blueprintTransferRules.js (a leaf module, so they are unit-testable).
//
// This is the asset-model replacement for the legacy whole-blueprint File-menu
// export (ENG-1030). That export predates blueprints being rows: it composes
// { body, face, events } out of the localStorage EDITOR CACHES, and its import
// overwrites those same three keys wholesale — which is the clobbering Florent
// hit ("if other characters have some existing stuff then it overrides it all").
//
// Two decisions do the work:
//
//   1. The unit is the CARD, not the character. A blueprint_asset row carries
//      exactly one graph (kind = body | face | events), so a file does too.
//
//   2. Export reads the SERVER row, not the editor cache — the same source the
//      card's own Duplicate already uses, for the reason its comment gives: a
//      copy must be of the graph on the card, not of whatever happens to be
//      open. It is also the only source the server-is-truth rule permits.
//
// And import CREATES A NEW CARD. That is the whole fix for the overwrite
// complaint: nothing existing is touched, so there is nothing to merge and no
// conflict UI to build. The author gets a new card and decides when — or
// whether — it becomes the character's default.

import { CustomAssetManager } from './CustomAssetManager.js'
import { serializeEnvelope } from './nodeClipboard.js'
import {
  buildCardEnvelope,
  exportFileNameFor,
  importedCardName,
  rowDocumentFor,
  validateImportText,
} from './blueprintTransferRules.js'

export { BLUEPRINT_EXPORT_EXT, exportFileNameFor } from './blueprintTransferRules.js'

/**
 * Build the export envelope for one blueprint card, fetching its graph from the
 * server. Throws on a fetch failure or an empty graph.
 */
export async function buildBlueprintCardExport(asset, { character } = {}) {
  const row = await CustomAssetManager.getBlueprintAssetContent(asset.rawId ?? asset.id)
  return buildCardEnvelope({ asset, row, character })
}

/** Export one card as a download. Returns the filename written. */
export async function downloadBlueprintCard(asset, { character } = {}) {
  const envelope = await buildBlueprintCardExport(asset, { character })
  const fileName = exportFileNameFor({ character, name: asset.name, kind: envelope.kind })
  const blob = new Blob([serializeEnvelope(envelope)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    a.click()
  } finally {
    URL.revokeObjectURL(url)
  }
  return { fileName, envelope }
}

/**
 * Read a file and validate it as a whole-graph blueprint export.
 * `{ ok, envelope }` or `{ ok: false, reason }`.
 */
export async function readBlueprintImportFile(file) {
  let text
  try {
    text = await file.text()
  } catch (e) {
    return { ok: false, reason: `could not read the file (${e?.message ?? e})` }
  }
  return validateImportText(text)
}

/**
 * Import a validated envelope as a NEW blueprint card.
 *
 * `setDefault` is deliberately NOT offered: making the imported graph the one
 * the character LOADS is a separate, visible decision the author takes on the
 * card ("Set as default"). Doing it here, as a side effect of a file drop,
 * would be exactly the silent overwrite this path exists to avoid.
 */
export async function importBlueprintEnvelope(envelope, { folderPath = '/blueprints', name } = {}) {
  const row = await CustomAssetManager.createBlueprintAsset({
    // uniqueAssetName inside createBlueprintAsset makes a repeat import a
    // second card rather than a name clash or an overwrite.
    name: importedCardName(envelope, name),
    kind: envelope.kind,
    folderPath,
    graph: rowDocumentFor(envelope),
    setDefault: false,
  })
  return { row, kind: envelope.kind, nodeCount: (envelope.graph?.nodes ?? []).length }
}
